// Default mauve colours for names that don't start with A-Z.
const DEFAULT_BACKGROUND = '#EBC5E4';
const DEFAULT_COLOR = '#5F3F5C';
const DEFAULT_WASH = 'rgba(180, 140, 175, 0.22)';

// Saturation / lightness lifted from the mauve trio above so every letter keeps
// the same brightness profile and only the hue rotates around the wheel.
const BG_SATURATION = 65;
const BG_LIGHTNESS = 85;
const TEXT_SATURATION = 35;
const TEXT_LIGHTNESS = 31;
const WASH_SATURATION = 35;
const WASH_LIGHTNESS = 63;
const WASH_ALPHA = 0.22;

// 26 letters spread evenly around the 360° hue wheel — A=0° (red),
// M=~166° (green-cyan), T=~263° (purple-blue), Z=~346° (back near pink).
const HUE_STEP = 360 / 26;

export interface AvatarColors {
  backgroundColor: string;
  color: string;
  /** Semi-transparent colour for background washes. */
  washColor: string;
}

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const hPrime = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hPrime % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hPrime < 1) [r, g, b] = [c, x, 0];
  else if (hPrime < 2) [r, g, b] = [x, c, 0];
  else if (hPrime < 3) [r, g, b] = [0, c, x];
  else if (hPrime < 4) [r, g, b] = [0, x, c];
  else if (hPrime < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = lNorm - c / 2;
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
};

const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (channel: number) => channel.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Avatar colours from the first letter of the first name (A-Z → 26 hues). Anything else gets the
 * default mauve. `fullName` is used when there's no first name.
 */
export const getAvatarColorsFromName = (firstName?: string | null, fullName?: string | null): AvatarColors => {
  const source = firstName?.trim() || fullName?.trim() || '';
  const firstChar = source[0]?.toUpperCase();
  if (!firstChar) {
    return { backgroundColor: DEFAULT_BACKGROUND, color: DEFAULT_COLOR, washColor: DEFAULT_WASH };
  }
  const code = firstChar.charCodeAt(0);
  // Only A-Z; accented letters fall back to mauve.
  if (code < 65 || code > 90) {
    return { backgroundColor: DEFAULT_BACKGROUND, color: DEFAULT_COLOR, washColor: DEFAULT_WASH };
  }
  const hue = (code - 65) * HUE_STEP;
  const [br, bg, bb] = hslToRgb(hue, BG_SATURATION, BG_LIGHTNESS);
  const [tr, tg, tb] = hslToRgb(hue, TEXT_SATURATION, TEXT_LIGHTNESS);
  const [wr, wg, wb] = hslToRgb(hue, WASH_SATURATION, WASH_LIGHTNESS);
  return {
    backgroundColor: rgbToHex(br, bg, bb),
    color: rgbToHex(tr, tg, tb),
    washColor: `rgba(${wr}, ${wg}, ${wb}, ${WASH_ALPHA})`,
  };
};
